import { forwardRef, useImperativeHandle, useRef } from "react";
import { Search, X } from "lucide-react";

import { cn } from "@lab/src/lib/util";
import {
	isMacPlatform,
	useKeyboardShortcut,
} from "../../hooks/useKeyboardShortcut";
import { IconInput } from "./icon-input.component";
import { Kbd } from "./kbd";

interface SearchInputProps {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	className?: string;
	/**
	 * Keyboard shortcut (e.g. "mod+shift+k") that focuses the input from
	 * anywhere on the page and renders a hint while the input is empty.
	 */
	shortcut?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
	(
		{ value, onChange, placeholder = "Suche...", className = "", shortcut },
		ref
	) => {
		const innerRef = useRef<HTMLInputElement>(null);
		useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

		useKeyboardShortcut(shortcut, () => {
			innerRef.current?.focus();
			innerRef.current?.select();
		});

		const showShortcutHint = !!shortcut && !value;

		return (
			<IconInput
				ref={innerRef}
				type="text"
				icon={<Search />}
				placeholder={placeholder}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				containerClassName={className}
				className={cn(
					showShortcutHint && (isMacPlatform() ? "pr-14" : "pr-20")
				)}
				trailing={
					value ? (
						<button
							onClick={() => onChange("")}
							className="pointer-events-auto text-gray-400 hover:text-gray-600"
							type="button"
							aria-label="Suche zurücksetzen"
						>
							<X className="h-4 w-4" />
						</button>
					) : showShortcutHint ? (
						<Kbd shortcut={shortcut} aria-hidden="true" />
					) : undefined
				}
			/>
		);
	}
);

SearchInput.displayName = "SearchInput";
